import { useCallback, useRef } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { GlobalJSBridgePayload, GlobalJSBridgeType } from "@swiggy-private/cart-types";
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

type CallBckRefType = {
    [key in GlobalJSBridgeType]: Function | undefined;
};

export const useGlobalJSBridgeHandler = () => {
    const callbackRef = useRef<CallBckRefType>({} as CallBckRefType);

    const jsBridgeCallbackHandler = useCallback((data: string) => {
        try {
            const payload = safeJsonParse<GlobalJSBridgePayload>(data) || { type: GlobalJSBridgeType.INVALID };
            if (!isEmpty(payload?.type) && typeof callbackRef.current[payload?.type] === "function") {
                return callbackRef.current[payload?.type]?.(payload);
            } else {
                throw new Error("Incorrect Payload Type or No Handler for this Payload Type");
            }
        } catch (e) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.GLOBAL_JS_BRIDGE_INCORRECT_TRIGGER,
                payload: data || "",
                errMessage: (e as Error)?.message || "",
            });
        }
    }, []);

    const registerCallbackForType = useCallback((type: GlobalJSBridgeType, handler: Function) => {
        callbackRef.current[type] = handler;
    }, []);

    const unregisterCallbackForType = useCallback((type: GlobalJSBridgeType, handler?: Function) => {
        if (typeof handler === "function") {
            callbackRef.current[type] = handler;
        } else {
            // setting undefined to track cases where some cleanup was done but still we got trigger to this function
            callbackRef.current[type] = undefined;
        }
    }, []);

    useEffectOnce(() => {
        window.globalJSBridgeHandler = jsBridgeCallbackHandler;
        return () => {
            window.globalJSBridgeHandler = () => {
                // no-op
            };
        };
    });

    return {
        registerCallbackForType,
        unregisterCallbackForType,
    };
};
